/** Task status presentation; only user-selectable reasons qualify as an explanation, never default filler. */
import { ColorUtility, EntityStatusStamp, StatusPickerOption, Task, TaskSystemStatusName } from '@jms/model';
import type { TagDeriverStyle } from '@workbuddy/ui/v2/surface.engine';

// Header tag for the task's status: the tenant's own status colour, with the reason/note the stamp carries as a tooltip.

const PENDING_TOOLTIP = 'Waiting for the line to be approved';

/** The Task detail status badge; `statuses` is the Tasks set (null or undefined until loaded) that decides whether the stamped reason is one a user could pick. */
export function taskStatusTag(value: unknown, model: Task, statuses: StatusPickerOption[] | null | undefined): TagDeriverStyle | null {

  // Boundary cast: the engine erases the bound field to unknown; the schema binds this renderer to taskStatus.
  const stamp = value as EntityStatusStamp | undefined;
  if (!stamp) return model.status ? { label: model.status } : null;

  return { label: stamp.name, tooltip: resolveTaskStatusTooltip(stamp, statuses), ...resolveTaskStatusColor(stamp) };
}

/** Used by the status renderer and tooltip to show saved explanations without exposing an editor. */
export function taskStatusExplanation(value: unknown, statuses: StatusPickerOption[] | null | undefined): { readonly reason?: string; readonly note?: string } {

  // Field renderers receive an erased field value; this one is bound only to taskStatus.
  const stamp = value as EntityStatusStamp | undefined;
  if (!stamp) return {};
  if (stamp.systemName === TaskSystemStatusName.Pending) return {};

  const status = statuses?.find(option => option._id === stamp._id);
  return {
    reason: isPickableReason(stamp, status) ? stamp.reason.name : undefined,
    note: stamp.note?.trim() ? stamp.note : undefined,
  };
}

function resolveTaskStatusTooltip(stamp: EntityStatusStamp, statuses: StatusPickerOption[] | null | undefined): string | undefined {

  if (stamp.systemName === TaskSystemStatusName.Pending) return PENDING_TOOLTIP;

  const explanation = taskStatusExplanation(stamp, statuses);
  const parts: string[] = [];
  if (explanation.reason) parts.push(`Reason: ${explanation.reason}`);
  if (explanation.note) parts.push(`Note: ${explanation.note}`);

  return parts.length ? parts.join('. ') : undefined;
}

/** The stamp carries the status's hidden default reason as filler when nobody chose one, so only a reason the picker offers for that status is an answer. */
function isPickableReason(stamp: EntityStatusStamp, status: StatusPickerOption | undefined): boolean {

  if (!stamp.reason) return false;

  // The set lists active statuses only; a deactivated or not yet loaded status keeps the old same-name rule.
  if (!status) return stamp.reason.name !== stamp.name;

  // A starting status offers no reasons, so whatever it carries is filler.
  if (status.isDefault) return false;

  const reasonId = stamp.reason._id;

  return status.reasons?.some(reason => reason._id === reasonId) ?? false;
}

function resolveTaskStatusColor(stamp: EntityStatusStamp): Pick<TagDeriverStyle, 'color' | 'backgroundColor' | 'borderColor'> {

  if (!stamp.color) return {};

  return { color: ColorUtility.idealTextColor(stamp.color), backgroundColor: stamp.color, borderColor: stamp.color };
}
